import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { createPortal } from "react-dom";
import { Link } from "@tanstack/react-router";
import {
  ChevronLeft,
  RotateCcw,
  SquarePen,
  Trash2,
  TriangleAlert,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { IssueActivityTrail } from "@/components/issues/issue-activity-trail/issue-activity-trail";
import { AddContributorButton } from "@/components/issues/add-contributor-sheet/add-contributor-button";
import { IssueContributors } from "@/components/issues/issue-contributors/issue-contributors";
import { DeleteIssueDialog } from "@/components/issues/delete-issue-dialog/delete-issue-dialog";
import { IssueEditSheet } from "@/components/issues/issue-edit-sheet/issue-edit-sheet";
import { IssueDatePill } from "@/components/issues/issue-date-pill/issue-date-pill";
import { IssueStatusPill } from "@/components/issues/issue-status-pill/issue-status-pill";
import { IssueUpdateSheet } from "@/components/issues/issue-update-sheet/issue-update-sheet";
import { PlanPinThumbnail } from "@/components/common/plan-pin-thumbnail/plan-pin-thumbnail";
import { OfflineImage } from "@/components/common/offline-image/offline-image";
import { IssuePhotoViewer } from "@/components/issues/issue-photo-viewer/issue-photo-viewer";
import {
  issuePhotos,
  type IssuePhoto,
} from "@/components/issues/issue-photo-viewer/issue-photos";
import { ExpandedPlanView } from "@/components/records/expanded-plan-view/expanded-plan-view";
import { useProjectPlans } from "@/components/plans/plans-view/use-project-plans";
import { useCurrentUser } from "@/hooks/use-current-user";
import { DESKTOP_QUERY } from "@/hooks/use-is-desktop";
import { useIssueRecord, useProjectIssue } from "@/hooks/use-issues";
import { useMediaQuery } from "@/hooks/use-media-query";
import { useCanOnResource, usePermissions } from "@/hooks/use-permissions";
import { useProject } from "@/hooks/use-project";
import {
  canCloseIssue,
  canEditIssue,
  canPostIssueUpdate,
  currentTargetDate,
  formatIssueNumber,
  issueStatusAction,
  overdueLabel,
  personName,
  showsIssueDates,
} from "@/lib/issues";
import { recordImages } from "@/lib/record-images";
import { cn } from "@/lib/utils";

// On a phone the page is the viewport; on a desktop it would otherwise stretch
// the full width of the browser, which leaves a paragraph of description running
// the width of a monitor. Cap it at the same 1100px the record surface uses so an
// issue and the record it points at read at one measure. Below that width the cap
// is a no-op and the column is simply the viewport.
// See [[issue-detail-page.spec]].
const contentColumn = "mx-auto w-full max-w-[1100px]";

interface IssueDetailProps {
  projectId: string;
  issueId: string;
  /**
   * Present when the issue is shown as the Issues page's side panel rather
   * than as its own page: the back arrow becomes a Close button, Escape closes,
   * and deleting closes the panel instead of navigating. See
   * [[issues-page.spec]] § Issue side panel.
   */
  onClose?: () => void;
  /**
   * With `onClose`: draw the close control as a ‹ back arrow with this
   * accessible name, rather than the panel's ✕. For a host that shows the
   * issue in place of its list on a phone — a level down, where an ✕ would
   * read as dismissing a dialog. See [[issue-detail-page.spec]] § Where it is
   * shown.
   */
  backLabel?: string;
  /**
   * With `backLabel`: the app bar's title names where the arrow goes — a
   * report's name — in place of the issue's number and title, which the
   * heading below already carries. See [[issue-detail-page.spec]] § Where it
   * is shown.
   */
  backTitle?: string;
}

/**
 * One issue — heading, evidence, location, people, activity and the action
 * bar. Rendered by the issue route as a full page, and by the Issues page and
 * a report as a right-side panel on desktop. See [[issue-detail-page.spec]].
 */
export function IssueDetail({
  projectId,
  issueId,
  onClose,
  backLabel,
  backTitle,
}: IssueDetailProps) {
  const { data: project } = useProject(projectId);
  const { data: issue, isLoading, error } = useProjectIssue(projectId, issueId);
  // Through the issue rather than the records endpoint, so the evidence and
  // the location show for anyone who can see the issue — a contributor holds
  // no record permissions. Keyed by the issue, so it loads alongside it. See
  // [[issue-detail-page.spec]].
  const { data: record } = useIssueRecord(projectId, issueId);
  const { data: currentUser } = useCurrentUser();
  const [updateOpen, setUpdateOpen] = useState(false);

  if (isLoading) {
    return (
      <DetailSkeleton
        projectId={projectId}
        onClose={onClose}
        backLabel={backLabel}
        backTitle={backTitle}
      />
    );
  }

  if (error || !issue) {
    return (
      <div className="flex h-full flex-col">
        <DetailAppBar
          projectId={projectId}
          title="Issue"
          onClose={onClose}
          backLabel={backLabel}
          backTitle={backTitle}
        />
        <div
          className={`${contentColumn} flex flex-1 items-center justify-center px-10 text-center`}
        >
          <p className="text-sm text-muted-foreground">
            This issue couldn&apos;t be loaded. It may have been removed with
            its record.
          </p>
        </div>
      </div>
    );
  }

  return (
    <IssueDetailContent
      projectId={projectId}
      issue={issue}
      record={record}
      project={project}
      currentUserId={currentUser?.id}
      updateOpen={updateOpen}
      setUpdateOpen={setUpdateOpen}
      onClose={onClose}
      backLabel={backLabel}
      backTitle={backTitle}
    />
  );
}

type IssueDetailContentProps = {
  projectId: string;
  issue: NonNullable<ReturnType<typeof useProjectIssue>["data"]>;
  record: ReturnType<typeof useIssueRecord>["data"];
  project: ReturnType<typeof useProject>["data"];
  currentUserId: string | undefined;
  updateOpen: boolean;
  setUpdateOpen: (open: boolean) => void;
  onClose?: () => void;
  backLabel?: string;
  backTitle?: string;
};

function IssueDetailContent({
  projectId,
  issue,
  record,
  project,
  currentUserId,
  updateOpen,
  setUpdateOpen,
  onClose,
  backLabel,
  backTitle,
}: IssueDetailContentProps) {
  // The project's plans — the same cached list the Plans tab reads, joined
  // client-side to the record's plan id for the location block below.
  const { data: plansData } = useProjectPlans(projectId);
  const [expandedPlan, setExpandedPlan] = useState(false);
  // One viewer for both photo sets — the record's evidence strip and the
  // trail's update photos — so a tap behaves the same wherever it lands, and
  // every other photo on the issue is a swipe or a thumbnail away. The list is
  // taken when the viewer opens, so a refetch while it is up (the record
  // landing after a trail tap, an update from another device) never shifts the
  // photos under the user. See [[issue-photo-viewer.spec]].
  const [viewer, setViewer] = useState<{
    photos: IssuePhoto[];
    index: number;
  } | null>(null);
  // Local rather than lifted: nothing above this component opens the edit
  // sheet or the delete dialog, and both triggers sit in this component's
  // own app bar.
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  // On desktop both sheets are centred dialogs — the update one the very
  // dialog a status pill on the Issues list opens, issue name and all — so a
  // panel's Update and pencil don't drop a sheet across a wide monitor.
  // Phones keep the bottom sheets. See [[issue-detail-page.spec]] § Where it
  // is shown.
  const isDesktop = useMediaQuery(DESKTOP_QUERY);
  const sheetPresentation = isDesktop ? "dialog" : "drawer";

  // As a side panel there is no dialog to give Escape for free, so listen for
  // it — leaving it to anything layered above (a sheet, a confirm, a
  // full-screen photo or plan) while one is up.
  const hasOverlayOpen =
    updateOpen || editOpen || deleteOpen || expandedPlan || viewer !== null;
  useEffect(() => {
    if (!onClose || hasOverlayOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      if (
        document.querySelector(
          '[role="dialog"], [role="alertdialog"], [role="menu"]',
        )
      ) {
        return;
      }
      onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, hasOverlayOpen]);

  // `usePermissions` wants the owner as an id; the detail read expands it to a
  // user object. Same shaping the project layout does. While the project
  // loads, assume nothing — showing an action that then vanishes is worse than
  // showing it a moment late.
  const permissionsProject = project
    ? { owner: project.owner.id, my_roles: project.my_roles }
    : { owner: "", my_roles: [] };
  const permissions = usePermissions(permissionsProject);
  const canOnResource = useCanOnResource(permissionsProject);
  // Whether the sheet may offer In progress and Completed. Posting on any
  // issue, or on an open one the viewer is involved in ([[permissions.spec]]
  // § All vs Involved scoping).
  const canUpdate = canPostIssueUpdate(issue, currentUserId, permissions.can);
  const canClose = canCloseIssue(issue, currentUserId, permissions.can);

  // The trash. Scoped Self/Other on the raiser like a record's delete; a
  // raiser whose account is gone resolves to Other, which is the backend's
  // rule too. Shown whatever the status — a closed issue raised in error is
  // still clutter — and hidden, never disabled, without the verb
  // ([[permissions]]). See [[issue-detail-page.spec]].
  const canDelete = canOnResource(
    "issue",
    "Delete",
    issue.created_by?.id ?? "",
  );

  // The bottom bar carries exactly one action, and which one follows the
  // status. Open: Update — anyone who can post any status at all gets it, and
  // which statuses it then offers is the sheet's business. Closed: Reopen, so a
  // mis-tapped close is recoverable without raising the issue again and losing
  // its history.
  //
  // Reopen is gated on posting to any issue rather than on `canClose`, because
  // the status it posts is `in_progress` and that is the backend's rule for
  // `in_progress`. A raiser who closed by identity alone therefore cannot
  // reopen, and neither can someone who posts only on issues they are
  // involved in — the asymmetry is the server's, and mirroring it beats
  // offering a button that comes back 403. See [[issues.spec]].
  //
  // The Issues list's swipe and status pill ask the same question, so the rule
  // lives in `issueStatusAction` rather than here.
  const isClosed = issue.status === "closed";
  const statusAction = issueStatusAction(issue, currentUserId, permissions.can);
  const showUpdate = statusAction === "update";
  const showReopen = statusAction === "reopen";
  // Beside Update, for anyone the server says may bring people on: whoever
  // can post on the issue, while it's open. The server's answer rather than
  // the client's, since only it knows who was invited. On a closed issue it is
  // false, and Reopen carries the bar alone. See [[issue-detail-page.spec]].
  const showAddContributor = issue.viewer_can_invite === true;

  // The pencil, in the app bar rather than the bottom slot: the primary action
  // stays the one that moves the work forward, and correcting a field is a
  // secondary act that should read as one.
  //
  // Hidden, never disabled ([[permissions]]), in two cases. Without
  // `issue:Edit` there is nothing to offer — the raiser gets the pencil by
  // holding the verb, never by identity, which is the opposite of how closing
  // works; and posting on an issue is not editing it. And on a closed issue
  // the header freezes: what a closed issue states is what was accepted, and
  // restating it without reopening is the one edit the record cannot survive.
  // Reopen first and the pencil comes back with it.
  // See [[issue-detail-page.spec]].
  const showEdit = !isClosed && canEditIssue(permissions.can);

  const photos = record ? recordImages(record) : [];
  const overdue = overdueLabel(issue);

  // The viewer runs oldest first while the trail runs newest first, so a tap is
  // placed by the photo's identity, never by its position on the page.
  const openPhoto = (imageId: string) => {
    const all = issuePhotos(issue, record);
    const index = all.findIndex((photo) => photo.id === imageId);
    setViewer({ photos: all, index: Math.max(index, 0) });
  };

  // The current target is read from the trail rather than off the issue, which
  // carries none of its own: it is the newest update that names a date, and
  // this page already holds every update, so it costs no extra request and
  // cannot fall out of step with the log the way a stored field could.
  //
  // Formatted through the same helper as the due date, so the two read as the
  // two facts of equal standing they are. Nothing compares them — a target
  // past the due date is not flagged, and the overdue callout keeps measuring
  // `due_date` alone. See [[issue-detail-page.spec]].
  const target = currentTargetDate(issue.updates);
  const showDates = showsIssueDates(issue);

  // Where on site this is. An issue has no plan or pin of its own — it inherits
  // the record's, which this page already fetched for the evidence strip, so
  // the block costs the backend nothing extra. The plan itself comes from the
  // project's cached plan list (the same query the Plans tab uses). A record
  // with no pin, or a plan outside the loaded pages, simply omits the block
  // rather than blocking the page on paging a plan list. See
  // [[issue-detail-page.spec]].
  const pinnedPlan = record?.plan
    ? plansData?.plans.find((candidate) => candidate.id === record.plan) ?? null
    : null;
  const location =
    pinnedPlan && record?.pin_x != null && record?.pin_y != null
      ? {
          plan: pinnedPlan,
          pinX: record.pin_x,
          pinY: record.pin_y,
          bearing: record.pin_bearing ?? null,
        }
      : null;

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <DetailAppBar
        projectId={projectId}
        onClose={onClose}
        backLabel={backLabel}
        backTitle={backTitle}
        title={`${formatIssueNumber(issue.number)} — ${issue.title}`}
        action={
          showEdit || canDelete ? (
            <div className="flex shrink-0 items-center gap-1">
              {showEdit && (
                <button
                  type="button"
                  aria-label="Edit issue"
                  onClick={() => setEditOpen(true)}
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-secondary text-secondary-foreground"
                >
                  <SquarePen className="h-[17px] w-[17px]" aria-hidden="true" />
                </button>
              )}
              {canDelete && (
                <button
                  type="button"
                  aria-label="Delete issue"
                  onClick={() => setDeleteOpen(true)}
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-secondary text-destructive"
                >
                  <Trash2 className="h-[17px] w-[17px]" aria-hidden="true" />
                </button>
              )}
            </div>
          ) : undefined
        }
      />

      <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-6">
        <div className={contentColumn}>
          <div className="flex flex-wrap items-center gap-2 py-3">
            <span className="text-xs font-medium text-muted-foreground">
              Issue {formatIssueNumber(issue.number)}
            </span>
            {/* The same chips in the same order and colours as the Issues list:
                status, target, due, then how late. See [[issue-detail-page.spec]]. */}
            <IssueStatusPill status={issue.status} />
            {showDates && (
              <>
                <IssueDatePill kind="target" value={target} tinted />
                <IssueDatePill
                  kind="due"
                  value={issue.due_date}
                  tinted
                  overdue={!!overdue}
                />
              </>
            )}
            {overdue && (
              <span className="inline-flex items-center gap-1 text-xs font-medium text-overdue">
                <TriangleAlert className="h-3 w-3" aria-hidden="true" />
                {overdue}
              </span>
            )}
          </div>

          <h1 className="text-lg font-semibold tracking-[-0.01em] [text-wrap:pretty]">
            {issue.title}
          </h1>

          {photos.length > 0 && (
            <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
              {photos.map((image) => (
                <button
                  key={image.id}
                  type="button"
                  aria-label="Open photo full screen"
                  onClick={() => openPhoto(image.id)}
                  className="shrink-0"
                >
                  {/* Through OfflineImage for its anonymous CORS: the photo
                      viewer loads these same S3 thumbnails with CORS, and a
                      plain no-CORS <img> here poisons that cache entry, so the
                      viewer's strip showed broken images. No offline key —
                      issue photos are not in the asset cache. See
                      [[s3-image-fetching]]. */}
                  <OfflineImage
                    assetKey=""
                    src={image.thumbnail_url ?? image.annotated_url ?? image.image}
                    alt=""
                    loading="lazy"
                    className="h-28 w-28 rounded-md object-cover"
                  />
                </button>
              ))}
            </div>
          )}

          {location && (
            <div className="mt-4">
              <h2 className="mb-2 text-sm font-semibold">Location</h2>
              <PlanPinThumbnail
                {...location}
                onExpand={() => setExpandedPlan(true)}
              />
            </div>
          )}

          {issue.description && (
            <p className="mt-3 text-sm leading-relaxed [text-wrap:pretty]">
              {issue.description}
            </p>
          )}

          <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
            <div>
              <dt className="text-xs text-muted-foreground">Assigned to</dt>
              <dd className="mt-0.5 font-medium">
                {issue.assigned_to ? personName(issue.assigned_to) : "Unassigned"}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">Raised by</dt>
              <dd className="mt-0.5 font-medium">
                {personName(issue.created_by)}
              </dd>
            </div>
          </dl>

          {/* Left out when the server sends no list (an older backend),
              rather than claiming nobody is on the issue. */}
          {issue.contributors && (
            <IssueContributors
              projectId={projectId}
              issueId={issue.id}
              contributors={issue.contributors}
              currentUserId={currentUserId}
              project={project}
            />
          )}

          <h2 className="mb-3 mt-6 text-sm font-semibold">Activity</h2>
          <IssueActivityTrail
            issue={issue}
            onOpenPhoto={(images, index) => openPhoto(images[index].id)}
          />
        </div>
      </div>

      {(showUpdate || showReopen || showAddContributor) && (
        <div className="shrink-0 border-t border-border p-4">
          <div
            className={cn(
              contentColumn,
              showAddContributor &&
                (showUpdate || showReopen) &&
                "grid grid-cols-2 gap-2.5",
            )}
          >
            {showAddContributor && (
              <AddContributorButton
                projectId={projectId}
                issue={issue}
                projectName={project?.name}
                currentUserId={currentUserId}
                presentation={sheetPresentation}
              />
            )}
            {/*
              Outline rather than filled on a closed issue: the bar then carries
              a correction, not the expected next step, and it should be
              reachable without reading as the thing to do.
            */}
            {(showUpdate || showReopen) && (
              <Button
                variant={showReopen ? "outline" : "default"}
                className="h-[46px] w-full"
                onClick={() => setUpdateOpen(true)}
              >
                {showReopen ? (
                  <>
                    <RotateCcw aria-hidden="true" />
                    Reopen
                  </>
                ) : (
                  "Update"
                )}
              </Button>
            )}
          </div>
        </div>
      )}

      <IssueUpdateSheet
        projectId={projectId}
        issue={issue}
        open={updateOpen}
        onOpenChange={setUpdateOpen}
        canClose={canClose}
        canUpdate={canUpdate}
        mode={showReopen ? "reopen" : "update"}
        presentation={sheetPresentation}
        showIssue={isDesktop}
      />

      {/*
        Mounted unconditionally, like the update sheet, and gated only by the
        pencil that opens it. Tying the mount to `showEdit` would unmount an
        OPEN sheet the moment a refetch showed the issue closed on another
        device — silently discarding everything typed, where the design calls
        for the save to be refused with a reason and the text kept.
        See [[issue-edit-sheet.spec]].
      */}
      <IssueEditSheet
        projectId={projectId}
        issue={issue}
        open={editOpen}
        onOpenChange={setEditOpen}
        presentation={sheetPresentation}
      />

      {/*
        Mounted unconditionally like the sheets and gated only by the trash
        that opens it. Its body mounts only while open, so a closed dialog
        costs the page none of its hooks. See [[delete-issue-dialog.spec]].
      */}
      <DeleteIssueDialog
        projectId={projectId}
        issue={issue}
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        onDeleted={onClose}
      />

      {/* Both full-screen surfaces below are portalled to the body. They are
          `fixed`, but a host that layers the issue — a report showing it in
          place of its list on a phone or a narrow desktop — gives this page a
          stacking context of its own, and inside it they were capped below
          the project header, which then covered their top bars and the
          Close and Compare in them. From the body they stand above the whole
          app wherever the issue is shown. See [[issue-detail-page.spec]]. */}

      {/* Tapping the location tile opens the same fullscreen surface the record
          viewer expands into — view-only here (no `isEditMode`), so an issue
          can never move the record's pin. */}
      {expandedPlan &&
        location &&
        createPortal(
          <ExpandedPlanView
            {...location}
            projectId={projectId}
            onClose={() => setExpandedPlan(false)}
          />,
          document.body,
        )}

      {viewer &&
        createPortal(
          <IssuePhotoViewer
            photos={viewer.photos}
            initialIndex={viewer.index}
            onClose={() => setViewer(null)}
          />,
          document.body,
        )}
    </div>
  );
}

function DetailAppBar({
  projectId,
  title,
  action,
  onClose,
  backLabel,
  backTitle,
}: {
  projectId: string;
  title: string;
  /** Side panel: a Close button replaces the back link. */
  onClose?: () => void;
  /** With `onClose`: the close control is a ‹ with this name instead of ✕. */
  backLabel?: string;
  /** With the ‹ above: shown in place of `title`, naming where it goes. */
  backTitle?: string;
  /** Sits at the right edge of the bar. Absent while the page is loading or
      errored — there is nothing yet to act on. */
  action?: ReactNode;
}) {
  return (
    <div className="shrink-0 px-2">
      <div className={`${contentColumn} flex h-14 items-center gap-2`}>
        {onClose && backLabel ? (
          // Same box as the back link below, so the arrow sits where the
          // host's own back arrow did.
          <button
            type="button"
            onClick={onClose}
            aria-label={backLabel}
            className="flex h-9 w-9 items-center justify-center"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
        ) : onClose ? (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close issue"
            className="flex h-9 w-9 items-center justify-center rounded-full transition-colors hover:bg-accent"
          >
            <X className="h-5 w-5" />
          </button>
        ) : (
          <Link
            to="/projects/$projectId/issues"
            params={{ projectId }}
            aria-label="Back to issues"
            className="flex h-9 w-9 items-center justify-center"
          >
            <ChevronLeft className="h-5 w-5" />
          </Link>
        )}
        <span className="min-w-0 flex-1 truncate text-base font-semibold tracking-[-0.01em]">
          {onClose && backLabel && backTitle ? backTitle : title}
        </span>
        {action}
      </div>
    </div>
  );
}

function DetailSkeleton({
  projectId,
  onClose,
  backLabel,
  backTitle,
}: {
  projectId: string;
  onClose?: () => void;
  backLabel?: string;
  backTitle?: string;
}) {
  return (
    <div className="flex h-full flex-col">
      {/* The app bar renders immediately so the page is escapable while it loads. */}
      <DetailAppBar
        projectId={projectId}
        title="Issue"
        onClose={onClose}
        backLabel={backLabel}
        backTitle={backTitle}
      />
      <div className={`${contentColumn} flex flex-col gap-3 px-4`}>
        <Skeleton className="h-6 w-40" />
        <Skeleton className="h-28 w-full rounded-md" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-2/3" />
      </div>
    </div>
  );
}
